// src/utils/companySettingsCache.js
import api from "../axiosConfig";

export const COMPANY_SETTINGS_CACHE_UPDATED_EVENT =
  "loadevo:company-settings-cache-updated";

const COMPANY_SETTINGS_CACHE_VERSION =
  "v2";

const COMPANY_SETTINGS_CACHE_MAX_AGE =
  180 *
  24 *
  60 *
  60 *
  1000;

const COMPANY_SETTINGS_FRESH_FOR =
  60 *
  1000;

const EMPTY_COMPANY_SETTINGS_DATA = {
  details: null,
  operations: null,
  financial: null,
  tax: null,
  fetchedAt: 0,
};

/*
 * Request state.
 *
 * Important:
 * - non-forced requests can share the same in-flight request;
 * - forced requests ALWAYS start a new request;
 * - only the most recent request is allowed to write into the cache.
 */
let inFlightPromise =
  null;

let requestSequence =
  0;

let latestRequestId =
  0;

  let warmedCompanyPhotoUrl =
  null;

let warmedCompanyPhotoImage =
  null;

let warmedCompanyPhotoPromise =
  null;

const resolveCompanyPhotoUrl =
  (
    details
  ) => {
    const raw =
      details?.photoCompany ||
      details?.photo_company ||
      null;

    if (!raw) {
      return null;
    }

    if (
      /^https?:\/\//i.test(
        raw
      )
    ) {
      return raw;
    }

    const base = (
      api.defaults?.baseURL ||
      ""
    ).replace(
      /\/$/,
      ""
    );

    return base
      ? `${base}${
          raw.startsWith("/")
            ? ""
            : "/"
        }${raw}`
      : raw;
  };

const prewarmCompanyPhoto =
  (
    data
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return Promise.resolve(
        null
      );
    }

    const url =
      resolveCompanyPhotoUrl(
        data?.details
      );

    if (!url) {
      warmedCompanyPhotoUrl =
        null;

      warmedCompanyPhotoImage =
        null;

      warmedCompanyPhotoPromise =
        null;

      return Promise.resolve(
        null
      );
    }

    /*
     * Já temos exatamente esta fotografia
     * aquecida/decoded.
     */
    if (
      warmedCompanyPhotoUrl ===
        url &&
      warmedCompanyPhotoPromise
    ) {
      return warmedCompanyPhotoPromise;
    }

    const image =
      new Image();

    image.crossOrigin =
      "anonymous";

    image.decoding =
      "sync";

    warmedCompanyPhotoUrl =
      url;

    warmedCompanyPhotoImage =
      image;

    warmedCompanyPhotoPromise =
      new Promise(
        (
          resolve
        ) => {
          const finish =
            async () => {
              try {
                if (
                  typeof image.decode ===
                  "function"
                ) {
                  await image.decode();
                }
              } catch {
                /*
                 * onload já significa que os bytes chegaram.
                 * Alguns browsers podem rejeitar decode()
                 * mesmo conseguindo apresentar a imagem.
                 */
              }

              resolve(
                url
              );
            };

          image.onload =
            finish;

          image.onerror =
            () => {
              if (
                warmedCompanyPhotoUrl ===
                url
              ) {
                warmedCompanyPhotoUrl =
                  null;

                warmedCompanyPhotoImage =
                  null;

                warmedCompanyPhotoPromise =
                  null;
              }

              resolve(
                null
              );
            };

          image.src =
            url;

          /*
           * Pode já estar na browser cache.
           */
          if (
            image.complete &&
            image.naturalWidth >
              0
          ) {
            finish();
          }
        }
      );

    return warmedCompanyPhotoPromise;
  };

/*
 * Incremented whenever we patch the cache locally.
 *
 * This prevents a network request that started before a local mutation
 * from finishing later and overwriting the newer local state.
 */
let localPatchRevision =
  0;

/*
 * Hot in-memory cache.
 *
 * localStorage continues to be the persistent cache, but components
 * should not need to parse localStorage repeatedly during the same
 * application session.
 *
 * Flow:
 *
 * memory -> immediate
 * localStorage -> persistence/reload
 * API -> background revalidation
 */
let hotCacheKey =
  null;

let hotCacheEnvelope =
  null;

const safeParse = (
  value
) => {
  try {
    return JSON.parse(
      value
    );
  } catch {
    return null;
  }
};

const decodeJwtPayload = (
  token
) => {
  if (!token) {
    return null;
  }

  try {
    const part =
      token
        .split(".")[1]
        ?.replace(
          /-/g,
          "+"
        )
        .replace(
          /_/g,
          "/"
        );

    if (!part) {
      return null;
    }

    const padded =
      part +
      "=".repeat(
        (
          4 -
          (
            part.length %
            4
          )
        ) %
          4
      );

    return JSON.parse(
      window.atob(
        padded
      )
    );
  } catch {
    return null;
  }
};

const hashString = (
  value
) => {
  let hash =
    2166136261;

  const source =
    String(
      value ||
        ""
    );

  for (
    let index = 0;
    index <
    source.length;
    index += 1
  ) {
    hash ^=
      source.charCodeAt(
        index
      );

    hash =
      Math.imul(
        hash,
        16777619
      );
  }

  return (
    hash >>>
    0
  ).toString(
    36
  );
};

const getStoredTokenWithoutImports =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return "";
    }

    return (
      window.localStorage.getItem(
        "authToken"
      ) ||
      window.sessionStorage.getItem(
        "authToken"
      ) ||
      window.localStorage.getItem(
        "token"
      ) ||
      window.localStorage.getItem(
        "accessToken"
      ) ||
      ""
    );
  };

const resolveCompanyScope =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return "server";
    }

    const token =
      getStoredTokenWithoutImports();

    const payload =
      decodeJwtPayload(
        token
      );

    const companyId =
      payload?.company_id ??
      payload?.companyId ??
      window.sessionStorage.getItem(
        "companyId"
      ) ??
      window.sessionStorage.getItem(
        "company_id"
      ) ??
      window.localStorage.getItem(
        "companyId"
      ) ??
      window.localStorage.getItem(
        "company_id"
      ) ??
      null;

    if (
      companyId !==
        null &&
      companyId !==
        undefined &&
      String(
        companyId
      ).trim() !==
        ""
    ) {
      return `company-${companyId}`;
    }

    if (
      token
    ) {
      return `token-${hashString(
        token
      )}`;
    }

    return "anonymous";
  };

const buildCacheKey =
  () =>
    `loadevo.company-settings.${resolveCompanyScope()}.${COMPANY_SETTINGS_CACHE_VERSION}`;

const normalizeResponseRow =
  (
    data
  ) => {
    if (
      Array.isArray(
        data
      )
    ) {
      return (
        data[0] ||
        {}
      );
    }

    return (
      data ||
      {}
    );
  };

const emitCacheUpdate =
  (
    data
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(
        COMPANY_SETTINGS_CACHE_UPDATED_EVENT,
        {
          detail:
            data,
        }
      )
    );
  };

  export const readCompanySettingsCache =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return null;
    }

    const cacheKey =
      buildCacheKey();

    /*
     * Fast path:
     *
     * If this company already has a cache loaded in memory,
     * avoid localStorage + JSON.parse completely.
     */
    if (
      hotCacheKey ===
        cacheKey &&
      hotCacheEnvelope?.data &&
      typeof hotCacheEnvelope.savedAt ===
        "number"
    ) {
      if (
        Date.now() -
          hotCacheEnvelope.savedAt <=
        COMPANY_SETTINGS_CACHE_MAX_AGE
      ) {
        return {
          ...EMPTY_COMPANY_SETTINGS_DATA,
          ...hotCacheEnvelope.data,
        };
      }

      hotCacheKey =
        null;

      hotCacheEnvelope =
        null;
    }

    try {
      const raw =
        window.localStorage.getItem(
          cacheKey
        );

      if (!raw) {
        return null;
      }

      const parsed =
        safeParse(
          raw
        );

      if (
        !parsed ||
        typeof parsed !==
          "object" ||
        !parsed.data ||
        typeof parsed.savedAt !==
          "number"
      ) {
        window.localStorage.removeItem(
          cacheKey
        );

        return null;
      }

      if (
        Date.now() -
          parsed.savedAt >
        COMPANY_SETTINGS_CACHE_MAX_AGE
      ) {
        window.localStorage.removeItem(
          cacheKey
        );

        return null;
      }

      hotCacheKey =
        cacheKey;

      hotCacheEnvelope =
        parsed;

      /*
       * A cache acabou de entrar na RAM.
       * Começamos imediatamente a preparar também
       * a fotografia associada.
       */
      prewarmCompanyPhoto(
        parsed.data
      ).catch(
        () => {
          // Best-effort preload only.
        }
      );

      return {
        ...EMPTY_COMPANY_SETTINGS_DATA,
        ...parsed.data,
      };
    } catch (
      error
    ) {
      console.warn(
        "[companySettingsCache] read failed:",
        error
      );

      return null;
    }
  };

  export const prewarmCompanySettingsPhotoFromCache =
  () => {
    const cached =
      readCompanySettingsCache();

    if (!cached) {
      return Promise.resolve(
        null
      );
    }

    return prewarmCompanyPhoto(
      cached
    );
  };

  export const writeCompanySettingsCache =
  (
    data
  ) => {
    if (
      typeof window ===
      "undefined"
    ) {
      return data;
    }

    const cacheKey =
      buildCacheKey();

    const nextData = {
      ...EMPTY_COMPANY_SETTINGS_DATA,
      ...(data || {}),
    };

    const envelope = {
      savedAt:
        Date.now(),

      data:
        nextData,
    };

    /*
     * Memory first.
     *
     * React subscribers can receive the new object immediately,
     * independently of persistent storage.
     */
    hotCacheKey =
      cacheKey;

    hotCacheEnvelope =
      envelope;

    /*
     * Se a fotografia mudou, começa imediatamente
     * download + decode da nova imagem.
     *
     * Não esperamos por isto para atualizar a UI.
     */
    prewarmCompanyPhoto(
      nextData
    ).catch(
      () => {
        // Best-effort preload only.
      }
    );

    try {
      window.localStorage.setItem(
        cacheKey,
        JSON.stringify(
          envelope
        )
      );
    } catch (
      error
    ) {
      console.warn(
        "[companySettingsCache] write failed:",
        error
      );
    }

    emitCacheUpdate(
      nextData
    );

    return nextData;
  };

export const patchCompanySettingsCache =
  (
    section,
    patch
  ) => {
    /*
     * Mark a local mutation before changing the cache.
     *
     * Any HTTP request which started before this mutation is no longer
     * allowed to overwrite the cache when it resolves.
     */
    localPatchRevision +=
      1;

    const current =
      readCompanySettingsCache() || {
        ...EMPTY_COMPANY_SETTINGS_DATA,
      };

    const previousSection =
      current?.[
        section
      ] &&
      typeof current[
        section
      ] ===
        "object"
        ? current[
            section
          ]
        : {};

    const nextSection =
      typeof patch ===
      "function"
        ? patch(
            previousSection
          )
        : {
            ...previousSection,
            ...(patch ||
              {}),
          };

    return writeCompanySettingsCache({
      ...current,

      [section]:
        nextSection,
    });
  };

export const clearCompanySettingsCache =
  () => {
    if (
      typeof window ===
      "undefined"
    ) {
      return;
    }

    localPatchRevision +=
      1;

    latestRequestId =
      ++requestSequence;

    inFlightPromise =
      null;

    const cacheKey =
      buildCacheKey();

    if (
      hotCacheKey ===
      cacheKey
    ) {
      hotCacheKey =
        null;

      hotCacheEnvelope =
        null;
    }

    try {
      window.localStorage.removeItem(
        cacheKey
      );
    } catch (
      error
    ) {
      console.warn(
        "[companySettingsCache] clear failed:",
        error
      );
    }

    emitCacheUpdate(
      null
    );
  };

  export const subscribeCompanySettingsCache =
  (
    callback
  ) => {
    if (
      typeof window ===
        "undefined" ||
      typeof callback !==
        "function"
    ) {
      return () => {};
    }

    const handler =
      (
        event
      ) => {
        callback(
          event?.detail ??
            readCompanySettingsCache()
        );
      };

    const storageHandler =
      (
        event
      ) => {
        const cacheKey =
          buildCacheKey();

        if (
          event.key !==
          cacheKey
        ) {
          return;
        }

        /*
         * Cache cleared in another tab.
         */
        if (
          !event.newValue
        ) {
          if (
            hotCacheKey ===
            cacheKey
          ) {
            hotCacheKey =
              null;

            hotCacheEnvelope =
              null;
          }

          callback(
            null
          );

          return;
        }

        const parsed =
          safeParse(
            event.newValue
          );

        if (
          !parsed ||
          typeof parsed !==
            "object" ||
          !parsed.data ||
          typeof parsed.savedAt !==
            "number"
        ) {
          return;
        }

        /*
         * Another LOADEvo tab wrote newer company settings.
         *
         * Bring this tab's hot cache up to date immediately.
         */
        hotCacheKey =
          cacheKey;

        hotCacheEnvelope =
          parsed;

        callback({
          ...EMPTY_COMPANY_SETTINGS_DATA,
          ...parsed.data,
        });
      };

    window.addEventListener(
      COMPANY_SETTINGS_CACHE_UPDATED_EVENT,
      handler
    );

    window.addEventListener(
      "storage",
      storageHandler
    );

    return () => {
      window.removeEventListener(
        COMPANY_SETTINGS_CACHE_UPDATED_EVENT,
        handler
      );

      window.removeEventListener(
        "storage",
        storageHandler
      );
    };
  };

const buildRequestConfig =
  (
    force,
    cacheBust
  ) => {
    if (
      !force
    ) {
      return undefined;
    }

    return {
      params: {
        _companySettingsRefresh:
          cacheBust,
      },

      headers: {
        "Cache-Control":
          "no-cache",

        Pragma:
          "no-cache",
      },
    };
  };

export const prefetchCompanySettingsData =
  async ({
    force = false,
  } = {}) => {
    const cached =
      readCompanySettingsCache();

    /*
     * Normal navigation:
     * use the fresh cache immediately.
     */
    if (
      !force &&
      cached?.fetchedAt &&
      Date.now() -
        cached.fetchedAt <
        COMPANY_SETTINGS_FRESH_FOR
    ) {
      return cached;
    }

    /*
     * Normal background prefetches can share an existing request.
     *
     * IMPORTANT:
     * force=true intentionally skips this block.
     *
     * Previously a forced refresh could receive a request that had
     * started BEFORE a profile/photo update, causing the old company
     * details to be written back over the new ones.
     */
    if (
      !force &&
      inFlightPromise
    ) {
      return inFlightPromise;
    }

    const requestId =
      ++requestSequence;

    /*
     * This request is now the newest authoritative network request.
     * Any older request is not allowed to commit into the cache.
     */
    latestRequestId =
      requestId;

    const patchRevisionAtStart =
      localPatchRevision;

    const cacheBust =
      Date.now();

    const requestConfig =
      buildRequestConfig(
        force,
        cacheBust
      );

    const requestPromise =
      (async () => {
        try {
          const [
            detailsResult,
            operationsResult,
            financialResult,
            taxResult,
          ] =
            await Promise.allSettled([
              api.get(
                "/company-details",
                requestConfig
              ),

              api.get(
                "/company-details/operations",
                requestConfig
              ),

              api.get(
                "/company/financial-information",
                requestConfig
              ),

              api.get(
                "/company-details/tax",
                requestConfig
              ),
            ]);

          /*
           * Another forced refresh started after this one.
           *
           * This response is therefore stale and MUST NOT write
           * into localStorage.
           */
          if (
            requestId !==
            latestRequestId
          ) {
            return (
              readCompanySettingsCache() ||
              cached ||
              null
            );
          }

          /*
           * A local patch happened while this HTTP request was running.
           *
           * Example:
           * 1. request starts with old company photo;
           * 2. user saves a new photo;
           * 3. cache is patched with new photo;
           * 4. old request finishes.
           *
           * Without this guard step 4 would restore the previous image.
           */
          if (
            patchRevisionAtStart !==
            localPatchRevision
          ) {
            return (
              readCompanySettingsCache() ||
              cached ||
              null
            );
          }

          const current =
            readCompanySettingsCache() || {
              ...EMPTY_COMPANY_SETTINGS_DATA,
            };

          const hasSuccess = [
            detailsResult,
            operationsResult,
            financialResult,
            taxResult,
          ].some(
            (
              result
            ) =>
              result.status ===
              "fulfilled"
          );

          if (
            !hasSuccess
          ) {
            return current;
          }

          const next = {
            ...current,

            details:
              detailsResult.status ===
              "fulfilled"
                ? normalizeResponseRow(
                    detailsResult
                      .value?.data
                  )
                : current.details,

            operations:
              operationsResult.status ===
              "fulfilled"
                ? normalizeResponseRow(
                    operationsResult
                      .value?.data
                  )
                : current.operations,

            financial:
              financialResult.status ===
              "fulfilled"
                ? normalizeResponseRow(
                    financialResult
                      .value?.data
                  )
                : current.financial,

            tax:
              taxResult.status ===
              "fulfilled"
                ? normalizeResponseRow(
                    taxResult
                      .value?.data
                  )
                : current.tax,

            fetchedAt:
              Date.now(),
          };

          /*
           * Check once more immediately before committing.
           *
           * This protects against a mutation occurring while the
           * response was being normalized.
           */
          if (
            requestId !==
              latestRequestId ||
            patchRevisionAtStart !==
              localPatchRevision
          ) {
            return (
              readCompanySettingsCache() ||
              cached ||
              null
            );
          }

          writeCompanySettingsCache(
            next
          );

          return next;
        } catch (
          error
        ) {
          console.warn(
            "[companySettingsCache] prefetch failed:",
            error
          );

          return (
            readCompanySettingsCache() ||
            cached ||
            null
          );
        }
      })();

    /*
     * Keep a reference to the most recent request.
     *
     * A forced request is still allowed to replace this reference;
     * the requestId protection above ensures the older request can no
     * longer overwrite its result.
     */
    inFlightPromise =
      requestPromise;

    try {
      return await requestPromise;
    } finally {
      /*
       * Do not clear a newer request that may have started while this
       * one was running.
       */
      if (
        inFlightPromise ===
        requestPromise
      ) {
        inFlightPromise =
          null;
      }
    }
  };